import { computed, type ComputedRef } from 'vue'

import { useSourceMediumPlatforms } from '../configs/platforms'

import { useRbac, useWorkspace } from '@/features/account'
import { useIntegrations } from '@/features/integrations'
import { useSettingsRbac } from '@/features/workspace-settings'

export interface UseDefinitionsRbac {
  canEdit: ComputedRef<boolean>
  canSeeConversions: ComputedRef<boolean>
  canSeeGa4KeyEvents: ComputedRef<boolean>
  canSeeHardalKeyEvents: ComputedRef<boolean>
  canSeeMmpKeyEvents: ComputedRef<boolean>
  canSeeCustomConversions: ComputedRef<boolean>
  canSeeTwitterConversions: ComputedRef<boolean>
  canSeeSourceMedium: ComputedRef<boolean>
  canSeeProduct: ComputedRef<boolean>
  canSeeGroupings: ComputedRef<boolean>
  canSeeKeywords: ComputedRef<boolean>
  canSeeMmpAcquisition: ComputedRef<boolean>
  hasChannel: (channel: string) => boolean
}

export function useDefinitionsRbac(): UseDefinitionsRbac {
  const rbac = useRbac()
  const settingsRbac = useSettingsRbac()
  const workspace = useWorkspace()
  const integrations = useIntegrations()
  const sourceMediumPlatforms = useSourceMediumPlatforms()
  const canSeeGa4KeyEvents = computed(
    () => integrations.isConnected('ga4') && rbac.hasPermission('settings_key_events'),
  )
  const canSeeHardalKeyEvents = computed(
    () => integrations.isConnected('hardal') && rbac.hasPermission('settings_key_events'),
  )
  const canSeeMmpKeyEvents = computed(
    () =>
      (integrations.isConnected('adjust') || integrations.isConnected('appsflyer')) &&
      rbac.hasPermission('settings_key_events'),
  )
  const canSeeCustomConversions = computed(
    () =>
      (integrations.isConnected('meta') || integrations.isConnected('google')) &&
      rbac.hasPermission('settings_custom_conversions'),
  )
  // Deliberate legacy predicate: Twitter has no settings_custom_conversions check (contract 30 §12).
  const canSeeTwitterConversions = computed(() => integrations.isConnected('twitter'))

  return {
    canEdit: settingsRbac.canEditWorkspace,
    canSeeConversions: computed(
      () =>
        canSeeGa4KeyEvents.value ||
        canSeeHardalKeyEvents.value ||
        canSeeMmpKeyEvents.value ||
        canSeeCustomConversions.value ||
        canSeeTwitterConversions.value,
    ),
    canSeeGa4KeyEvents,
    canSeeHardalKeyEvents,
    canSeeMmpKeyEvents,
    canSeeCustomConversions,
    canSeeTwitterConversions,
    canSeeSourceMedium: computed(
      () =>
        sourceMediumPlatforms.value.length > 0 &&
        integrations.isConnected('analytics') &&
        rbac.hasPermission('settings_source_medium'),
    ),
    canSeeProduct: computed(
      () =>
        workspace.workspace.value?.is_ecommerce === true &&
        rbac.hasPermission('settings_product_customization'),
    ),
    canSeeGroupings: computed(() => rbac.hasPermission('settings_campaign_grouping')),
    canSeeKeywords: computed(() => rbac.hasPermission('settings_branded_keywords')),
    canSeeMmpAcquisition: computed(
      () =>
        (integrations.isConnected('adjust') || integrations.isConnected('appsflyer')) &&
        rbac.hasPermission('mmp_acquisition'),
    ),
    hasChannel: integrations.isConnected,
  }
}
